import { Howl } from 'howler';
import WordTimingsService from './word_timings_service'
import audioStorageService from './audio_storage_service'
import { usesSegmentedReadingAid } from '../../reading_aids/reading_aid_languages'

function scaleWordTimings(wordTimings, scale) {
  if (scale === 1 || !wordTimings || !Array.isArray(wordTimings.word_start_times_ms)) return wordTimings;

  return {
    ...wordTimings,
    word_start_times_ms: wordTimings.word_start_times_ms.map(time => time * scale),
    word_durations_ms: (wordTimings.word_durations_ms || []).map(time => time * scale)
  };
}

class AudioPlayerService {
  constructor(csrfToken) {
    this.csrfToken = csrfToken;
    this.wordTimings;
    this.wordTimingsService = new WordTimingsService();
    this.soundPlaying = false;
    this.player = null; // Store the current sound object
    this.wordElements;
    this.activeWord;
    this.activeSentence = null;
    this.animationFrameId = null;
    this.wordElement;
    this.soundEl;
    this.highlightingMode = 'sentence';
    this.speedOptions = {
      "slow": 0.8,
      "standard": 1.0,
      "fast": 1.5,
    }
    this.audioPool = [];
    this.maxPoolSize = 5;
    this.outputDeviceId = null;
    this.ttsContext = null; // TTS context for silent transliteration languages
  }

  playText(text, language, soundEl, otherOptions = {}, soundSyncEnabled = true) {

    if (this.soundPlaying) {
      this.stopSound(soundEl);
    }

    if (soundEl) {
      soundEl.classList.add('pulse');
    }

    this.fetchAudioData(text, language, otherOptions)
    .then(data => {
      soundEl.classList.remove('pulse');

      const { audioUrl, wordTimings, playbackRate: serverPlaybackRate } = data;

      if (otherOptions.chat_message_id) {
        audioStorageService.setAudioUrl(otherOptions['chat_message_id'], audioUrl);
        soundEl.dataset.audioKey = audioStorageService.getStorageKey(otherOptions['chat_message_id']);
      } else {
        soundEl.dataset.audioUrl = audioUrl;
      }

      // Replays read this, so they keep the audio's speed (LAN-1177).
      if (serverPlaybackRate) {
        soundEl.dataset.playbackRate = serverPlaybackRate;
      }

      if (wordTimings && soundSyncEnabled) {
        this.wordTimings = wordTimings;
        const playbackRate = serverPlaybackRate || this.speedOptions[otherOptions.speed] || 1.0;
        this.setupWordElements(this.wordTimings, this.wordElement);
        this.playSound(audioUrl, soundEl, playbackRate);
        this.setupSync();
      } else {
        this.playSound(audioUrl, soundEl, serverPlaybackRate || this.speedOptions[otherOptions.speed]);
      }
    })
    .catch(error => {
      console.error('Error playing audio:', error);
      this.stopLoading(soundEl);

      // Show user-friendly error message for audio failures
      this.showAudioError(I18n.t('javascript.errors.audio_unavailable'));
    });
  }

  fetchAudioData(text, language, otherOptions) {
    const that = this;

    return fetch('/langua/stream_text_to_speech', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      credentials: 'same-origin',
      // Web applies X-Langua-Playback-Rate below, so it opts into Eleven v4 (LAN-1177)
      body: JSON.stringify({ text: text, language: language, ...otherOptions, ttsPlaybackRate: true })
    })
    .then(response => {
      const contentType = response.headers.get('Content-Type');
      // Eleven v4 audio has no speed setting, so Rails sends the rate to play
      // it at (the user's speed tier). No header: play as before (LAN-1177).
      const playbackRate = parseFloat(response.headers.get('X-Langua-Playback-Rate')) || null;
      
      // Check if response is binary audio (Cartesia/OpenAI) or JSON (ElevenLabs)
      if (contentType && contentType.includes('audio/mpeg')) {
        // Handle binary MP3 response (Cartesia/OpenAI)
        return response.blob().then(blob => {
          const audioUrl = URL.createObjectURL(blob);
          // No word timings available for binary response
          return { audioUrl, wordTimings: null, playbackRate };
        });
      } else {
        // Handle JSON response with timestamps (ElevenLabs)
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let audioBytes = "";
        let alignmentData = { chars: [], charStartTimesMs: [], charEndTimesMs: [], charDurationsMs: []};
        let buffer = "";
   
        const processChunk = ({ done, value }) => {
          if (done) {
            const byteArray = new Uint8Array(audioBytes.length);
            for (let i = 0; i < audioBytes.length; i++) {
              byteArray[i] = audioBytes.charCodeAt(i);
            }
            const audioBlob = new Blob([byteArray.buffer], { type: 'audio/mp3' });
            const audioUrl = URL.createObjectURL(audioBlob);
            const wordTimings = that.wordTimingsService.processNormalizedAlignment(alignmentData);

            return { audioUrl, wordTimings, playbackRate };
          }

          buffer += decoder.decode(value, { stream: true });
          let lines = buffer.split('\n');
   
          // Process each complete line as a JSON object
          for (let i = 0; i < lines.length - 1; i++) {
            const jsonString = lines[i].trim();
            if (jsonString) {
              try {
                const responseDict = JSON.parse(jsonString);
                const audioBytesChunk = window.atob(responseDict.audio_base64);
                audioBytes += audioBytesChunk;
   
                if (responseDict.alignment) {
                  alignmentData.chars.push(...responseDict.alignment.characters);
                  alignmentData.charStartTimesMs.push(...responseDict.alignment.character_start_times_seconds.map(time => time * 1000));
                  alignmentData.charEndTimesMs.push(...responseDict.alignment.character_end_times_seconds.map(time => time * 1000));
                  alignmentData.charDurationsMs = alignmentData.charEndTimesMs.map((endTime, i) => (endTime - alignmentData.charStartTimesMs[i]));
                }
              } catch (e) {
                console.error('Error parsing JSON chunk:', e);
              }
            }
          }
   
          // If the last line is incomplete, keep it in the buffer
          buffer = lines[lines.length - 1].trim();

          return reader.read().then(processChunk);
        };

        return reader.read().then(processChunk);
      }
    });
  }

  playSound(url, soundEl, playbackRate = 1.0) {
    this.setupPlayer(url, soundEl, playbackRate);
    this.player.play();
  }

  // timingScale stretches the word timings for audio that was itself stretched
  // (the server's slowed copy of a reply, LAN-1177).
  setupAudio(wordElement, element, text = null, language = null, otherOptions = {}, soundSyncEnabled = true, playbackRate = 1.0, timingScale = 1) {
    try {
      this.wordElement = wordElement;

      // Skip word highlighting for Japanese and Chinese
      const skipWordHighlighting = usesSegmentedReadingAid(language);

      if (this.soundPlaying) {
        if (this.soundEl == element) {
          this.togglePlayer();
          return;
        } else {
          this.stopSound(this.soundEl);
          this.soundEl = element;
        }
      } else {
        this.soundEl = element;
      }

      const audioKey = this.soundEl.dataset.audioKey;
      const audioUrl = this.soundEl.dataset.audioUrl;

      // Read TTS context from data attribute (for silent transliteration languages on page refresh)
      if (this.soundEl.dataset.ttsContext) {
        try {
          this.ttsContext = JSON.parse(this.soundEl.dataset.ttsContext);
        } catch (e) {
          this.ttsContext = null;
        }
      } else {
        this.ttsContext = null;
      }

      if (this.soundEl.dataset.wordTimings && !skipWordHighlighting) {
        this.wordTimings = scaleWordTimings(JSON.parse(this.soundEl.dataset.wordTimings), timingScale);
      }

      if (audioKey) {
        const chatMessageId = audioStorageService.extractChatMessageId(audioKey);
        const audioUrl = audioStorageService.getAudioUrl(chatMessageId);
        if (audioUrl) {
          if (this.wordTimings && !skipWordHighlighting) {
            this.setupPlayerWithSync(audioUrl, this.soundEl, language, playbackRate);
          } else {
            this.playSound(audioUrl, this.soundEl, playbackRate);
          }
        }
      } else if (audioUrl != 'null' && audioUrl != undefined) {
        if (this.wordTimings && !skipWordHighlighting) {
          this.setupPlayerWithSync(audioUrl, this.soundEl, language, playbackRate);
        } else {
          this.playSound(audioUrl, this.soundEl, playbackRate);
        }
      } else if (this.soundEl.dataset.audioData != 'null' && this.soundEl.dataset.audioData != undefined) {
        const audioData = JSON.parse(this.soundEl.dataset.audioData);
        const decodedAudioData = audioData.map((data) => {
          return window.atob(data);
        })
        const combinedAudioData = decodedAudioData.join('');
        const byteArray = new Uint8Array(combinedAudioData.length);

        for (let i = 0; i < combinedAudioData.length; i++) {
          byteArray[i] = combinedAudioData.charCodeAt(i);
        }
        const audioBlob = new Blob([byteArray.buffer], { type: 'audio/mp3' });
        const audioUrl = URL.createObjectURL(audioBlob);

        this.wordElement = this.soundEl.closest('.chat-message');

        if (this.wordElement !== null && this.wordElement !== undefined) {
          const chatMessageId = this.wordElement.dataset.chatMessageId;
          audioStorageService.setAudioUrl(chatMessageId, audioUrl);
          this.soundEl.dataset.audioKey = audioStorageService.getStorageKey(chatMessageId);
        }

        this.setupPlayerWithSync(audioUrl, this.soundEl, language, playbackRate);
      } else {
        if (this.soundEl.dataset.chatMessageId) {
          otherOptions.chat_message_id = this.soundEl.dataset.chatMessageId;
        }
        this.playText(text, language, this.soundEl, otherOptions, soundSyncEnabled);
      }
    } catch (error) {
      console.error('Error setting up audio:', error); 
    }
  }

  setupPlayerWithSync(url, soundEl, language, playbackRate = 1.0) {
    // Skip word highlighting setup for Japanese and Chinese
    if (!usesSegmentedReadingAid(language)) {
      this.setupWordElements(this.wordTimings, this.wordElement);
      this.setupPlayer(url, soundEl, playbackRate);
      this.setupSync();
    } else {
      this.setupPlayer(url, soundEl, playbackRate);
    }

    this.player.play();
  }

  setupPlayer(url, soundEl, playbackRate = 1.0) {

    if (this.soundPlaying) {
      this.stopSound(soundEl);
    }

    this.soundEl = soundEl;

    let availablePlayer = this.getAvailablePlayer(url, playbackRate);

    if (!availablePlayer) {
      if (this.audioPool.length >= this.maxPoolSize) {
        this.audioPool.shift().unload(); // Unload the oldest player
      }

      // preserve soundEl innerHTML
      const soundElIcon = soundEl.innerHTML;

      availablePlayer = new Howl({
        html5: true,
        src: [url],
        format: ['mp3'],
        rate: playbackRate,
        onplay: () => {
          if (this.outputDeviceId !== null) {
            const audioElement = availablePlayer._sounds[0]._node;

            if (typeof audioElement.setSinkId === 'function') {
              audioElement
                .setSinkId(this.outputDeviceId)
                .then(() => {
                  console.log('Audio output successfully redirected.');
                })
                .catch((error) => {
                  console.error('Failed to redirect audio output:', error);
                });
            }
          }

          this.soundPlaying = true;

          if (soundEl.classList.contains('js-user-chat-listen-yourself-btn')) {
            soundEl.innerHTML = '<i class="fa fa-stop"></i>';
          } else {
            soundEl.innerHTML = '<i class="fa fa-pause"></i>';
          }
  
          if (!soundEl.classList.contains('js-user-chat-listen-yourself-btn')) {
            document.querySelectorAll('.js-user-chat-listen-yourself-btn').forEach(btn => {
              btn.innerHTML = '<i class="fa fa-play"></i>';
            });
          }

          this.emitPlayStarted();
        },
        onend: () => {
          this.stopLoading(soundEl);
          soundEl.innerHTML = soundElIcon;
        },
        onpause: () => {
          soundEl.innerHTML = '<i class="fa fa-play"></i>';
        },
        onstop: () => {
          if (!soundEl.classList.contains('js-user-chat-listen-yourself-btn')) {
            soundEl.innerHTML = '<i class="fa fa-play"></i>';
          }
        }
      });

      this.audioPool.push(availablePlayer);
    }

    this.player = availablePlayer;
  }

  togglePlayer() {
    if (this.player === null) {
      return
    }

    if (this.player.playing()) {
      this.player.pause();
    } else {
      this.player.play();
    }
  }

  toggleAudio(audioUrl, audioEl) {   
    try {
      if (this.soundPlaying) {
        this.stopSound(this.soundEl);

        if (this.soundEl == audioEl) {
          return;
        }
      }

      this.soundEl = audioEl;

      if (audioUrl) {
        this.setupPlayer(audioUrl, audioEl);
        this.player.play();
      }
    } catch (error) {
      throw error;
    }
  }

  getAvailablePlayer(url, playbackRate) {
    for (let player of this.audioPool) {
      if (player._src === url && player._rate === playbackRate && !player.playing()) {
        return player;
      }
    }
    return null;
  }

  stopSound(soundEl) {
    if (this.player) {
      this.player.stop();
      this.player.unload();
    }
    this.stopLoading(soundEl);
  }

  stopLoading(soundEl) {
    this.soundPlaying = false;
    if (soundEl) {
      soundEl.innerHTML = '<i class="fa fa-play"></i>'
      soundEl.classList.remove('spin-loading', 'pulse');
    }
    this.player = null;
  }

  reset() {
    this.wordTimings = null;
    this.sentenceBoundaries = null;
    this.ttsContext = null; // Clear TTS context on reset

    for (let word of this.wordElements) {
      word.style.backgroundColor = "";
      word.style.color = "";  // Reset the color when the word is no longer active.
      word.style.transition = "";
      word.style.borderRadius = "";
    }

    try {
      if (this.activeWord !== null && this.activeWord !== undefined) {
        this.activeWord.style.backgroundColor = "";
        this.activeWord.style.color = "";  // Reset the color when the word is no longer active.
        this.activeWord.style.transition = "";
        this.activeWord.style.borderRadius = "";
        this.activeWord = null;
      }
    } catch (error) {
      console.error('Error resetting active wordt:', error);
    }

    this.activeSentence = null;

    this.animationFrameId = null;
    this.soundPlaying = false;
  }

  setupSync() {
    this.player.on('play', () => {
      // Kickstart the animation loop
      this.animationFrameId = requestAnimationFrame(this.handleTimeUpdate.bind(this));
    });

    // Stop tracking when the audio is paused or ended
    this.player.on('pause', () => {
      cancelAnimationFrame(this.animationFrameId);
    });

    this.player.on('end', () => {
      cancelAnimationFrame(this.animationFrameId);
      this.reset();
    });

    this.player.on('playerror', (error) => {
      console.error('Error playing audio:', error);
      cancelAnimationFrame(this.animationFrameId);
      this.player.unload();
      this.reset();
      this.showAudioError(I18n.t('javascript.errors.audio_playback_failed'));
    });

    this.player.on('loaderror', (error) => {
      console.error('Error playing audio:', error);
      cancelAnimationFrame(this.animationFrameId);
      this.reset();
      this.showAudioError(I18n.t('javascript.errors.audio_load_failed'));
    });
  }

  updateActiveWord(currentTime) {
    // Skip highlighting if mode is off
    if (this.highlightingMode === 'off') {
      return false;
    }

    if (this.highlightingMode === 'sentence') {
      return this.updateActiveSentence(currentTime);
    }

    // Original word highlighting logic
    if (this.activeWord !== null && this.activeWord !== undefined) {
      const wordStartTime = parseFloat(this.activeWord.dataset.startTime);
      const wordEndTime = parseFloat(this.activeWord.dataset.endTime);
      if (currentTime >= wordStartTime && currentTime <= wordEndTime) {
        return true;
      }
    }

    for (let word of this.wordElements) {
      const wordStartTime = parseFloat(word.dataset.startTime);
      const wordEndTime = parseFloat(word.dataset.endTime);

      if (currentTime >= wordStartTime && currentTime <= wordEndTime) {
        if (this.activeWord && this.activeWord.id === word.id) {
          return true;
        }

        if (this.activeWord !== null && this.activeWord !== undefined) {
          this.activeWord.style.backgroundColor = "";
          this.activeWord.style.color = "";
          this.activeWord.style.transition = "";
        }

        this.activeWord = word;

        // Don't apply highlighting styles if the word has challenge class (audio-only mode)
        if (!word.classList.contains('challenge')) {
          this.activeWord.style.backgroundColor = "#E5ECF4";
          this.activeWord.style.color = "#333";
          this.activeWord.style.transition = "none";
        } else {
          // For challenge mode, just add a subtle border or different background
          // that doesn't reveal the text
          this.activeWord.style.backgroundColor = "rgba(61, 71, 82, 0.3)";
          this.activeWord.style.transition = "none";
        }

        break;
      } else {
        word.style.backgroundColor = "";
        word.style.color = "";  // Reset the color when the word is no longer active.
        word.style.transition = "";
      }
    }
  }

  updateActiveSentence(currentTime) {
    // Find the current word based on timing
    let currentWord = null;
    for (let word of this.wordElements) {
      const wordStartTime = parseFloat(word.dataset.startTime);
      const wordEndTime = parseFloat(word.dataset.endTime);

      if (currentTime >= wordStartTime && currentTime <= wordEndTime) {
        currentWord = word;
        break;
      }
    }

    if (!currentWord) {
      return false;
    }

    // Get the sentence containing the current word
    const currentSentenceWords = this.getSentenceContainingWord(currentWord);

    // Check if we're already highlighting the correct sentence
    if (this.activeSentence && this.activeSentence.length === currentSentenceWords.length &&
      this.activeSentence.every(word => currentSentenceWords.includes(word))) {
      return true;
    }

    // Clear previous sentence highlighting
    if (this.activeSentence) {
      for (let word of this.activeSentence) {
        word.style.backgroundColor = "";
        word.style.color = "";
        word.style.transition = "";
        word.style.borderRadius = "";
      }
    }

    // Clear all other words
    for (let word of this.wordElements) {
      if (!currentSentenceWords.includes(word)) {
        word.style.backgroundColor = "";
        word.style.color = "";
        word.style.transition = "";
        word.style.borderRadius = "";
      }
    }

    // Highlight the new sentence with proper border radius
    this.activeSentence = currentSentenceWords;
    for (let i = 0; i < this.activeSentence.length; i++) {
      const word = this.activeSentence[i];

      // Don't apply highlighting styles if the word has challenge class (audio-only mode)
      if (!word.classList.contains('challenge')) {
        word.style.backgroundColor = "#E5ECF4";
        word.style.color = "#333";
        word.style.transition = "none";
      } else {
        // For challenge mode, just add a subtle background that doesn't reveal the text
        word.style.backgroundColor = "rgba(61, 71, 82, 0.3)";
        word.style.transition = "none";
      }

      // Apply border radius based on position in sentence
      if (this.activeSentence.length === 1) {
        // Single word sentence - rounded on both sides
        word.style.borderRadius = "4px";
      } else if (i === 0) {
        // First word - rounded left only
        word.style.borderRadius = "4px 0 0 4px";
      } else if (i === this.activeSentence.length - 1) {
        // Last word - rounded right only
        word.style.borderRadius = "0 4px 4px 0";
      } else {
        // Middle words - no rounded edges
        word.style.borderRadius = "0";
      }
    }

    // Keep track of the active word for other functionality
    this.activeWord = currentWord;

    return true;
  }

  handleTimeUpdate() {
    if (this.player === null) {
      return;
    }

    const currentTime = this.player.seek();
    this.updateActiveWord(currentTime);

    // Continue the animation loop
    this.animationFrameId = requestAnimationFrame(this.handleTimeUpdate.bind(this));
  }

  setupWordElements(wordTimings, wordElement) {
    this.wordElements = Array.from(wordElement.querySelectorAll('.js-audio-word.word'));
    this.sentenceBoundaries = null; // Reset sentence boundaries when word elements change

    // Check if wordTimings exists and has the expected structure
    if (!wordTimings || !wordTimings.words || !Array.isArray(wordTimings.words)) {
      console.warn('Word timings not available or invalid structure');
      return this.wordElements;
    }

    // Check if we need special alignment for silent transliteration languages
    const hasSilentTransliterations = this.ttsContext &&
      this.ttsContext.topicCategory === 'guided' &&
      this.isSilentTransliterationLanguage(this.ttsContext.language);

    if (hasSilentTransliterations) {
      // Use special alignment that skips transliteration words
      this.setupWordElementsWithTransliterationAlignment(wordTimings);
    } else {
      // Standard 1:1 mapping
      for (let i = 0; i < wordTimings.words.length; i++) {
        const startTime = wordTimings.word_start_times_ms[i];
        const duration = wordTimings.word_durations_ms[i];

        const wordElement = this.wordElements[i];
        if (wordElement) {
          // Howler.seek() reports audio seconds, independent of playback rate.
          wordElement.dataset.startTime = (startTime / 1000).toFixed(3);
          wordElement.dataset.endTime = ((startTime + duration) / 1000).toFixed(3);
          wordElement.id = `word-${i}`;
        }
      }
    }

    return this.wordElements;
  }

  /**
   * Check if language has silent transliterations (stripped before TTS)
   * These languages show transliterations on screen but don't speak them
   */
  isSilentTransliterationLanguage(language) {
    const silentTransliterationLanguages = ['russian', 'korean', 'hindi', 'greek'];
    return language && silentTransliterationLanguages.includes(language.toLowerCase());
  }

  /**
   * Setup word elements with special alignment for silent transliteration languages.
   * Word timings are based on text without transliterations, but display includes them.
   */
  setupWordElementsWithTransliterationAlignment(wordTimings) {
    // Get words from display elements (includes transliterations)
    const displayWords = this.wordElements.map(el => {
      const text = el.textContent || el.innerText || '';
      return text.trim();
    });

    // Get words from spoken text (no transliterations) - these match word timings
    const spokenWords = wordTimings.words;

    let spokenIdx = 0;
    let displayIdx = 0;
    let insideTransliteration = false; // Track if we're inside a (...) block

    while (displayIdx < displayWords.length) {
      const displayWord = displayWords[displayIdx];
      const wordElement = this.wordElements[displayIdx];

      // Check if this word starts a transliteration block
      if (displayWord.startsWith('(')) {
        insideTransliteration = true;
      }

      // Check if this display word is part of a transliteration
      const isTransliteration = insideTransliteration;

      // Check if this word ends a transliteration block
      if (displayWord.endsWith(')')) {
        insideTransliteration = false;
      }

      if (isTransliteration) {
        // Skip transliteration words - they weren't spoken
        // Give them zero duration at the previous spoken word's end time
        if (wordElement) {
          const prevEndTime = spokenIdx > 0
            ? (wordTimings.word_start_times_ms[spokenIdx - 1] + wordTimings.word_durations_ms[spokenIdx - 1]) / 1000
            : 0;
          wordElement.dataset.startTime = prevEndTime.toFixed(3);
          wordElement.dataset.endTime = prevEndTime.toFixed(3);
          wordElement.id = `word-${displayIdx}`;
        }
        displayIdx++;
        continue;
      }

      // This is a real word - apply timing if we have spoken words left
      if (spokenIdx < spokenWords.length) {
        if (wordElement) {
          const startTime = wordTimings.word_start_times_ms[spokenIdx];
          const duration = wordTimings.word_durations_ms[spokenIdx];
          wordElement.dataset.startTime = (startTime / 1000).toFixed(3);
          wordElement.dataset.endTime = ((startTime + duration) / 1000).toFixed(3);
          wordElement.id = `word-${displayIdx}`;
        }
        spokenIdx++;
      } else {
        // No more spoken words - give remaining display words the last end time
        const lastEndTime = spokenWords.length > 0
          ? (wordTimings.word_start_times_ms[spokenWords.length - 1] + wordTimings.word_durations_ms[spokenWords.length - 1]) / 1000
          : 0;
        if (wordElement) {
          wordElement.dataset.startTime = lastEndTime.toFixed(3);
          wordElement.dataset.endTime = lastEndTime.toFixed(3);
          wordElement.id = `word-${displayIdx}`;
        }
      }

      displayIdx++;
    }
  }

  updateSoundOutput(deviceId) {
    this.outputDeviceId = deviceId;
    for (let player of this.audioPool) {
      if(player !== null && player._sounds !== null && player._sounds !== undefined) {
        if (player._sounds.length > 0) {
          player._sounds[0]._node.setSinkId(deviceId);
        }
      }
    }
  }

  emitPlayEnded() {
    // Emit the playended event
    const event = new CustomEvent('chat:audioended');
    document.dispatchEvent(event);
  }

  emitPlayStarted() {
    // Emit the playended event
    const event = new CustomEvent('chat:audiostarted');
    document.dispatchEvent(event);
  }

  setHighlightingMode(mode) {
    this.highlightingMode = mode;

    // Clear any existing highlighting when switching to off mode
    if (mode === 'off') {
      this.clearAllHighlighting();
    }
  }

  clearAllHighlighting() {
    // Clear word highlighting
    if (this.wordElements) {
      for (let word of this.wordElements) {
        word.style.backgroundColor = "";
        word.style.color = "";
        word.style.transition = "";
        word.style.borderRadius = "";
      }
    }

    // Clear active word
    if (this.activeWord !== null && this.activeWord !== undefined) {
      this.activeWord.style.backgroundColor = "";
      this.activeWord.style.color = "";
      this.activeWord.style.transition = "";
      this.activeWord.style.borderRadius = "";
      this.activeWord = null;
    }

    // Clear active sentence
    if (this.activeSentence) {
      for (let word of this.activeSentence) {
        word.style.backgroundColor = "";
        word.style.color = "";
        word.style.transition = "";
        word.style.borderRadius = "";
      }
      this.activeSentence = null;
    }
  }

  getHighlightingMode() {
    return this.highlightingMode;
  }

  findSentenceBoundaries(wordElements) {
    const sentences = [];
    let currentSentence = [];
    const endOfSentenceRegex = /[.!?。！？។៕។…\u3002\uFF01\uFF1F\u0964\u0965]/;

    for (let i = 0; i < wordElements.length; i++) {
      const word = wordElements[i];
      currentSentence.push(word);

      // Check if this word ends with sentence-ending punctuation
      const wordText = word.textContent || word.innerText || '';
      if (endOfSentenceRegex.test(wordText)) {
        sentences.push([...currentSentence]);
        currentSentence = [];
      }
    }

    // Add any remaining words as the last sentence
    if (currentSentence.length > 0) {
      sentences.push(currentSentence);
    }

    return sentences;
  }

  getSentenceContainingWord(word) {
    if (!this.sentenceBoundaries) {
      this.sentenceBoundaries = this.findSentenceBoundaries(this.wordElements);
    }

    for (const sentence of this.sentenceBoundaries) {
      if (sentence.includes(word)) {
        return sentence;
      }
    }

    return [word]; // Fallback to single word if no sentence found
  }

  // Show user-friendly error message for audio failures
  showAudioError(message) {
    // Create or get existing error message element
    let errorEl = document.getElementById('audio-error-message');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.id = 'audio-error-message';
      errorEl.className = 'audio-error-notification';
      errorEl.style.cssText = `
        background-color: #f8d7da;
        border: 1px solid #f5c6cb;
        color: #721c24;
        padding: 8px 12px;
        border-radius: 6px;
        margin: 8px 0;
        font-size: 13px;
        line-height: 1.3;
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1050;
        max-width: 300px;
        opacity: 0;
        transform: translateX(100%);
        transition: all 0.3s ease;
      `;

      // Add to document
      document.body.appendChild(errorEl);
    }

    // Update message text
    errorEl.textContent = message;

    // Show the message with animation
    setTimeout(() => {
      errorEl.style.opacity = '1';
      errorEl.style.transform = 'translateX(0)';
    }, 10);

    // Auto-hide after 4 seconds
    setTimeout(() => {
      if (errorEl && errorEl.parentNode) {
        errorEl.style.opacity = '0';
        errorEl.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (errorEl && errorEl.parentNode) {
            errorEl.parentNode.removeChild(errorEl);
          }
        }, 300);
      }
    }, 4000);
  }

}

export default AudioPlayerService;
